import React, { useState, useCallback, useRef, useLayoutEffect } from 'react';
import { Globe, User, ChevronDown, Check, X, ArrowUp, ArrowDown } from 'lucide-react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useTheme } from '../../../contexts/ThemeContext';
import type { Document } from '../../../services/api';
import type { MentionType } from '../MentionTypes';
import type { DocSectionProps } from './types';
import DocTooltip, { useDocTooltip } from '../../../components/sidebar/DocTooltip';
import FilterDropdown from './FilterDropdown';

/**
 * SortTag — inline component for clickable sort chips with direction toggle.
 * First click activates with default direction, second click inverts.
 */
const SortTag: React.FC<{
  label: string;
  field: string;
  currentSort: string;
  onSort: (order: string) => void;
}> = ({ label, field, currentSort, onSort }) => {
  const { theme } = useTheme();
  const isActive = currentSort === field || currentSort === `-${field}`;
  const isDesc = currentSort === `-${field}`;

  const handleClick = () => {
    if (!isActive) {
      // First click: activate with sensible default (desc for date, asc for text)
      onSort(field === 'created_at' ? `-${field}` : field);
    } else {
      // Toggle direction
      onSort(isDesc ? field : `-${field}`);
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-0.5 text-xs transition-colors rounded-lg px-1.5 py-0.5 min-h-[44px] ${
        isActive
          ? `font-medium ${theme.colors.text.primary} ${theme.colors.bg.secondary}`
          : `${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary}`
      }`}
      title={
        isActive
          ? `${label}: ${isDesc ? 'descendente' : 'ascendente'} — click para invertir`
          : `Ordenar por ${label}`
      }
      aria-label={
        isActive
          ? `${label}: ${isDesc ? 'descendente' : 'ascendente'} — click para invertir`
          : `Ordenar por ${label}`
      }
    >
      {label}
      {isActive &&
        (isDesc ? <ArrowDown className="w-2.5 h-2.5" /> : <ArrowUp className="w-2.5 h-2.5" />)}
    </button>
  );
};

import { formatPeriod, formatPeriodForFilter } from '../../../utils/formatPeriod';

/** Format period for display: quarterly label + real date+time for HE, quarterly label only for others */
function formatPeriodWithTime(doc: Document): string {
  const period = doc.document_period || '';
  const quarterLabel = formatPeriod(period, doc.document_type);

  // HE: show quarter label + real CMF publication date+time (e.g., "1T 2026 · 27 feb 06:11 p. m.")
  if (doc.document_type === 'HE' && doc.published_at) {
    const published = new Date(doc.published_at);
    const dateTime =
      published.toLocaleDateString('es-CL', { day: 'numeric', month: 'short' }) +
      ' ' +
      published.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' });
    return quarterLabel ? `${quarterLabel} · ${dateTime}` : dateTime;
  }

  // FECU/ANALISIS: show quarter label (e.g., "1T 2025")
  return quarterLabel;
}

/**
 * DocSection — a scope-aware document list section with filter chips, sort tags, and footer.
 * Search bar and recentes are managed by the shell (DocumentContextSelector).
 * Renders flat content (no internal scroll) — participates in parent scroll.
 */
const DocSection: React.FC<DocSectionProps> = ({
  documents,
  isLoading,
  searchTerm,
  sectionScope,
  toggleDocumentSelection,
  selectDocumentRange,
  handleSelectAll,
  handleDeselectAll,
  handleFilterClick,
  handleFilterValueSelect,
  handleRemoveFilter,
  handleClearAllFilters,
  loadFilterSuggestions,
  formatDocumentMetadata,
  hasFilters,
  selectedCount,
  selectedDocumentIds,
  scrollContainerRef,
  filters,
  filterSuggestions,
  isLoadingFilters,
  totalDocCount,
  sortOrder,
  onSortChange,
}) => {
  const { theme } = useTheme();
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const lastClickedIndexRef = useRef<number>(-1);
  const virtualListRef = useRef<HTMLDivElement>(null);
  const { tooltip, onMouseEnter: onDocMouseEnter, onMouseLeave: onDocMouseLeave } = useDocTooltip();

  // Inline filter dropdown state
  const [activeFilter, setActiveFilter] = useState<MentionType | null>(null);
  const [filterAnchorRect, setFilterAnchorRect] = useState<DOMRect | null>(null);

  // Virtual scrolling — only render visible rows (~10-15) instead of all 500
  // useLayoutEffect recalculates scrollMargin after DOM commits, fixing stale
  // values when virtualListRef remounts (e.g., after loading spinner disappears).
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    if (!virtualListRef.current || !scrollContainerRef?.current) return;
    const containerTop = scrollContainerRef.current.getBoundingClientRect().top;
    const listTop = virtualListRef.current.getBoundingClientRect().top;
    const scrollTop = scrollContainerRef.current.scrollTop;
    setScrollMargin(Math.max(0, listTop - containerTop + scrollTop));
  }, [documents.length, isLoading, searchTerm, hasFilters]);

  const rowVirtualizer = useVirtualizer({
    count: documents.length,
    getScrollElement: () => scrollContainerRef?.current ?? null,
    estimateSize: () => 44,
    overscan: 8,
    scrollMargin,
  });

  const handleFilterValueSelectLocal = useCallback(
    (value: string) => {
      handleFilterValueSelect(value);
      setSelectedIndex(-1);
    },
    [handleFilterValueSelect]
  );

  // Open inline filter dropdown
  const openFilterDropdown = useCallback(
    (filterType: MentionType, e: React.MouseEvent) => {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      setFilterAnchorRect(rect);
      setActiveFilter(filterType);
      handleFilterClick(filterType); // triggers loadFilterSuggestions in hook
    },
    [handleFilterClick]
  );

  const closeFilterDropdown = useCallback(() => {
    setActiveFilter(null);
    setFilterAnchorRect(null);
  }, []);

  return (
    <div className="flex flex-col">
      {/* Toolbar: unified single-row with filters (▾) and sort (↑↓) separated by │.
           Both share the same text-button base style. Filters use ChevronDown affordance,
           sort uses ArrowUp/ArrowDown. Active state: accent bg for filters, secondary bg for sort. */}
      {(sectionScope === 'shared'
        ? hasFilters || (!searchTerm && documents.length > 0)
        : !searchTerm && documents.length > 0) && (
        <div
          className={`px-3 py-1.5 border-b ${theme.colors.border.primary} sticky top-0 z-10 ${theme.colors.bg.primary}`}
        >
          <div className="flex items-center gap-1 flex-wrap">
            {/* ── Filter buttons (shared scope only) ── */}
            {sectionScope === 'shared' &&
              (() => {
                const companyArr = Array.isArray(filters.company)
                  ? filters.company
                  : filters.company
                    ? [filters.company]
                    : [];
                const hasCompany = companyArr.length > 0;
                const companyLabel =
                  companyArr.length === 0
                    ? 'Empresa'
                    : companyArr.length === 1
                      ? companyArr[0]
                      : `${companyArr[0]} +${companyArr.length - 1}`;

                const typeArr = Array.isArray(filters.document_type)
                  ? filters.document_type
                  : filters.document_type
                    ? [filters.document_type]
                    : [];
                const hasType = typeArr.length > 0;
                const typeLabel =
                  typeArr.length === 0
                    ? 'Tipo'
                    : typeArr.length === 1
                      ? typeArr[0]
                      : `${typeArr[0]} +${typeArr.length - 1}`;

                const periodArr = Array.isArray(filters.document_period)
                  ? filters.document_period
                  : filters.document_period
                    ? [filters.document_period]
                    : [];
                const hasPeriod = periodArr.length > 0;
                const periodLabel =
                  periodArr.length === 0
                    ? 'Fecha'
                    : periodArr.length === 1
                      ? formatPeriodForFilter(periodArr[0])
                      : `${formatPeriodForFilter(periodArr[0])} +${periodArr.length - 1}`;

                const sectorArr = Array.isArray(filters.shared_category)
                  ? filters.shared_category
                  : filters.shared_category
                    ? [filters.shared_category]
                    : [];
                const hasSector = sectorArr.length > 0;
                const sectorLabel =
                  sectorArr.length === 0
                    ? 'Sector'
                    : sectorArr.length === 1
                      ? sectorArr[0]
                      : `${sectorArr[0]} +${sectorArr.length - 1}`;

                const filterBase =
                  'inline-flex items-center gap-0.5 text-xs transition-colors rounded-lg px-2 py-1.5 min-h-[44px]';
                const filterInactive = `${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary}`;
                const filterActive = 'bg-nb-accent/10 text-nb-accent';

                return (
                  <>
                    <button
                      onClick={(e) => openFilterDropdown('empresa', e)}
                      className={`${filterBase} ${hasCompany ? filterActive : filterInactive}`}
                      title={
                        hasCompany ? `Empresa: ${companyArr.join(', ')}` : 'Filtrar por empresa'
                      }
                      aria-label={
                        hasCompany ? `Empresa: ${companyArr.join(', ')}` : 'Filtrar por empresa'
                      }
                    >
                      <span className="truncate max-w-[80px]">{companyLabel}</span>
                      <ChevronDown className="w-2.5 h-2.5 opacity-60" />
                      {hasCompany && (
                        <button
                          type="button"
                          aria-label="Quitar filtro empresa"
                          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFilter('empresa', e as any);
                          }}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </button>
                    <span className={`text-[9px] ${theme.colors.text.muted}`}>·</span>
                    <button
                      onClick={(e) => openFilterDropdown('tipo', e)}
                      className={`${filterBase} ${hasType ? filterActive : filterInactive}`}
                      title={hasType ? `Tipo: ${typeArr.join(', ')}` : 'Filtrar por tipo'}
                      aria-label={hasType ? `Tipo: ${typeArr.join(', ')}` : 'Filtrar por tipo'}
                    >
                      <span className="truncate max-w-[80px]">{typeLabel}</span>
                      <ChevronDown className="w-2.5 h-2.5 opacity-60" />
                      {hasType && (
                        <button
                          type="button"
                          aria-label="Quitar filtro tipo"
                          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFilter('tipo', e as any);
                          }}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </button>
                    <span className={`text-[9px] ${theme.colors.text.muted}`}>·</span>
                    <button
                      onClick={(e) => openFilterDropdown('fecha', e)}
                      className={`${filterBase} ${hasPeriod ? filterActive : filterInactive}`}
                      title={
                        hasPeriod
                          ? `Fecha: ${periodArr.map(formatPeriodForFilter).join(', ')}`
                          : 'Filtrar por fecha'
                      }
                      aria-label={
                        hasPeriod
                          ? `Fecha: ${periodArr.map(formatPeriodForFilter).join(', ')}`
                          : 'Filtrar por fecha'
                      }
                    >
                      <span className="truncate max-w-[80px]">{periodLabel}</span>
                      <ChevronDown className="w-2.5 h-2.5 opacity-60" />
                      {hasPeriod && (
                        <button
                          type="button"
                          aria-label="Quitar filtro fecha"
                          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFilter('fecha', e as any);
                          }}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </button>
                    <span className={`text-[9px] ${theme.colors.text.muted}`}>·</span>
                    <button
                      onClick={(e) => openFilterDropdown('sector', e)}
                      className={`${filterBase} ${hasSector ? filterActive : filterInactive}`}
                      title={hasSector ? `Sector: ${sectorArr.join(', ')}` : 'Filtrar por sector'}
                      aria-label={
                        hasSector ? `Sector: ${sectorArr.join(', ')}` : 'Filtrar por sector'
                      }
                    >
                      <span className="truncate max-w-[80px]">{sectorLabel}</span>
                      <ChevronDown className="w-2.5 h-2.5 opacity-60" />
                      {hasSector && (
                        <button
                          type="button"
                          aria-label="Quitar filtro sector"
                          className="min-h-[44px] min-w-[44px] inline-flex items-center justify-center -m-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFilter('sector', e as any);
                          }}
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </button>
                    {/* Separator between filters and sort */}
                    {!searchTerm && documents.length > 0 && (
                      <span className={`text-xs mx-0.5 ${theme.colors.text.muted}`}>│</span>
                    )}
                  </>
                );
              })()}
            {/* ── Sort buttons ── */}
            {!searchTerm && documents.length > 0 && (
              <>
                <SortTag
                  label="Reciente"
                  field="created_at"
                  currentSort={sortOrder}
                  onSort={onSortChange}
                />
                <span className={`text-[9px] ${theme.colors.text.muted}`}>·</span>
                <SortTag
                  label="Nombre"
                  field="name"
                  currentSort={sortOrder}
                  onSort={onSortChange}
                />
                {sectionScope === 'shared' && (
                  <>
                    <span className={`text-[9px] ${theme.colors.text.muted}`}>·</span>
                    <SortTag
                      label="Empresa"
                      field="company_name"
                      currentSort={sortOrder}
                      onSort={onSortChange}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Documents list — always visible */}
      <div>
        {isLoading ? (
          <div className={`px-4 py-8 text-center ${theme.colors.text.secondary}`}>
            <div className="animate-spin w-4 h-4 border-2 border-nb-accent border-t-transparent rounded-full mx-auto mb-2" />
            <div className="text-xs">Cargando documentos...</div>
          </div>
        ) : documents.length === 0 ? (
          <div className={`px-4 py-8 text-center ${theme.colors.text.secondary}`}>
            <Globe className="w-6 h-6 mx-auto mb-2 opacity-50" />
            <div className="text-xs">
              {searchTerm ? `Sin resultados para "${searchTerm}"` : 'No hay documentos disponibles'}
            </div>
            {hasFilters && (
              <div className="mt-3 text-xs opacity-70">
                <div className="mb-2">
                  Filtros activos:
                  {filters.company &&
                    (Array.isArray(filters.company) ? filters.company : [filters.company]).map(
                      (c) => (
                        <span
                          key={c}
                          className="inline-flex items-center ml-1 px-1.5 py-0.5 rounded-lg bg-nb-accent/10 text-nb-accent text-xs"
                        >
                          {c}
                        </span>
                      )
                    )}
                  {filters.document_type &&
                    (Array.isArray(filters.document_type)
                      ? filters.document_type
                      : [filters.document_type]
                    ).map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center ml-1 px-1.5 py-0.5 rounded-lg bg-nb-accent/10 text-nb-accent text-xs"
                      >
                        {t}
                      </span>
                    ))}
                  {filters.document_period &&
                    (Array.isArray(filters.document_period)
                      ? filters.document_period
                      : [filters.document_period]
                    ).map((p) => (
                      <span
                        key={p}
                        className="inline-flex items-center ml-1 px-1.5 py-0.5 rounded-lg bg-nb-accent/10 text-nb-accent text-xs"
                      >
                        {formatPeriodForFilter(p)}
                      </span>
                    ))}
                  {filters.shared_category &&
                    (Array.isArray(filters.shared_category)
                      ? filters.shared_category
                      : [filters.shared_category]
                    ).map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center ml-1 px-1.5 py-0.5 rounded-lg bg-nb-accent/10 text-nb-accent text-xs"
                      >
                        {s}
                      </span>
                    ))}
                </div>
                <button
                  onClick={handleClearAllFilters}
                  className="text-nb-accent hover:underline text-xs"
                >
                  Quitar filtros
                </button>
              </div>
            )}
            {!hasFilters && !searchTerm && (
              <div className="mt-4 text-xs opacity-70">
                {sectionScope === 'private'
                  ? 'Sube documentos privados desde el menú +'
                  : 'Usa los filtros arriba o busca por nombre'}
              </div>
            )}
          </div>
        ) : (
          <div className="py-2">
            <div
              ref={virtualListRef}
              style={{
                height: rowVirtualizer.getTotalSize(),
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const doc = documents[virtualRow.index];
                const index = virtualRow.index;
                const isSelected = selectedDocumentIds.includes(doc.id);
                const isHighlighted = index === selectedIndex;

                return (
                  <div
                    key={doc.id}
                    data-index={virtualRow.index}
                    ref={rowVirtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${
                        virtualRow.start - rowVirtualizer.options.scrollMargin
                      }px)`,
                    }}
                    onClick={(e) => {
                      if (e.shiftKey && lastClickedIndexRef.current !== -1) {
                        const start = Math.min(lastClickedIndexRef.current, index);
                        const end = Math.max(lastClickedIndexRef.current, index);
                        const rangeIds = documents.slice(start, end + 1).map((d) => d.id);
                        selectDocumentRange(rangeIds);
                      } else {
                        toggleDocumentSelection(doc.id);
                        lastClickedIndexRef.current = index;
                      }
                    }}
                    role="checkbox"
                    aria-checked={isSelected}
                    aria-label={`${doc.name || doc.original_filename || 'Documento sin nombre'}${
                      isSelected ? ' (seleccionado)' : ''
                    }`}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        toggleDocumentSelection(doc.id);
                      }
                    }}
                    className={`group/doc px-3 py-1.5 cursor-pointer flex items-center gap-2 transition-colors hover:z-10 ${
                      isHighlighted ? theme.colors.bg.secondary : ''
                    } hover:${theme.colors.bg.secondary} ${
                      isSelected ? `${theme.colors.bg.secondary} border-l-2 border-nb-accent` : ''
                    }`}
                    onMouseEnter={(e) => onDocMouseEnter(doc, e)}
                    onMouseLeave={onDocMouseLeave}
                  >
                    <div
                      className={`flex-shrink-0 transition-opacity ${
                        isSelected
                          ? 'text-nb-accent'
                          : `${theme.colors.text.secondary} opacity-0 group-hover/doc:opacity-100`
                      }`}
                    >
                      {isSelected ? (
                        <div
                          className={`w-4 h-4 rounded flex items-center justify-center ${theme.colors.bg.primary} border-2 border-nb-accent`}
                        >
                          <Check className="w-2.5 h-2.5 text-nb-accent" />
                        </div>
                      ) : (
                        <div
                          className={`w-4 h-4 rounded border-2 ${theme.colors.border.primary}`}
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-medium ${theme.colors.text.primary} truncate`}>
                        {doc.short_name ||
                          doc.company_name ||
                          doc.name ||
                          doc.original_filename ||
                          'Documento sin nombre'}
                      </div>
                      {(doc.document_type || doc.document_period || doc.shared_category) && (
                        <div className="flex items-center gap-1 mt-0.5 min-w-0">
                          {doc.document_type && (
                            <span
                              className={`text-xs font-medium flex-shrink-0 px-1 rounded-lg ${
                                doc.document_type === 'FECU'
                                  ? 'text-blue-400 bg-blue-400/10'
                                  : doc.document_type === 'ANALISIS'
                                    ? 'text-emerald-400 bg-emerald-400/10'
                                    : doc.document_type === 'HE'
                                      ? 'text-amber-400 bg-amber-400/10'
                                      : theme.colors.text.secondary
                              }`}
                            >
                              {doc.document_type}
                            </span>
                          )}
                          {doc.shared_category && (
                            <>
                              {doc.document_type && (
                                <span className={`text-xs ${theme.colors.text.muted}`}>•</span>
                              )}
                              <span className={`text-xs flex-shrink-0 ${theme.colors.text.muted}`}>
                                {doc.shared_category}
                              </span>
                            </>
                          )}
                          {doc.document_period && (doc.document_type || doc.shared_category) && (
                            <span className={`text-xs ${theme.colors.text.muted}`}>•</span>
                          )}
                          {doc.document_period && (
                            <span
                              className={`text-xs flex-shrink-0 ${theme.colors.text.secondary}`}
                            >
                              {formatPeriodWithTime(doc)}
                            </span>
                          )}
                          {doc.subject && (
                            <>
                              <span className={`text-xs ${theme.colors.text.muted}`}>•</span>
                              <span
                                className={`text-xs ${theme.colors.text.muted} truncate`}
                                title={doc.subject}
                              >
                                {doc.subject}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    {/* Scope badge — only show when scope differs from section (avoids redundancy) */}
                    {doc.scope !== sectionScope && (
                      <div className={`flex-shrink-0 ${theme.colors.text.muted}`}>
                        {doc.scope === 'shared' ? (
                          <Globe className="w-3.5 h-3.5" />
                        ) : (
                          <User className="w-3.5 h-3.5" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Inline filter dropdown (portal) */}
      {activeFilter && filterAnchorRect && (
        <FilterDropdown
          key={activeFilter}
          filterType={activeFilter}
          anchorRect={filterAnchorRect}
          suggestions={filterSuggestions}
          isLoading={isLoadingFilters}
          onSearch={(query) => loadFilterSuggestions(activeFilter, query)}
          onSelect={(value) => {
            handleFilterValueSelectLocal(value);
            // All filter types are multi-select — user clicks "Listo" or outside to close.
          }}
          onClose={closeFilterDropdown}
          selectedValues={(() => {
            const raw =
              activeFilter === 'empresa'
                ? filters.company
                : activeFilter === 'tipo'
                  ? filters.document_type
                  : activeFilter === 'fecha'
                    ? filters.document_period
                    : activeFilter === 'sector'
                      ? filters.shared_category
                      : undefined;
            return Array.isArray(raw) ? raw : raw ? [raw] : [];
          })()}
        />
      )}

      {/* Tooltip */}
      {tooltip && <DocTooltip doc={tooltip.doc} anchor={tooltip.rect} />}

      {/* Compact footer */}
      <div
        className={`px-3 py-1 border-t ${theme.colors.border.primary} flex items-center justify-between`}
      >
        <span className={`text-xs ${theme.colors.text.secondary}`}>
          {selectedCount > 0
            ? `${selectedCount} sel.`
            : `${documents.length} doc${documents.length !== 1 ? 's' : ''}`}
        </span>
        <div className="flex items-center gap-1.5">
          {documents.length > 0 && (
            <>
              <button
                onClick={handleSelectAll}
                className={`text-xs px-1.5 py-0.5 rounded-lg min-h-[44px] ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors`}
              >
                Todos
              </button>
              {selectedCount > 0 && (
                <button
                  onClick={handleDeselectAll}
                  className={`text-xs px-1.5 py-0.5 rounded-lg min-h-[44px] ${theme.colors.text.secondary} hover:${theme.colors.text.primary} hover:${theme.colors.bg.secondary} transition-colors`}
                >
                  Ninguno
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocSection;
